<template>
    <div class="workflow-page">
        <el-card shadow="never">
            <div slot="header" class="clearfix"
                style="display:flex; justify-content:space-between; align-items:center;">
                <span>项目列表</span>
                <div>
                    <el-input v-model="searchKeyword" placeholder="搜索项目名称" size="small" clearable
                        style="width: 220px; margin-right: 10px;" />
                    <el-button size="small" type="primary" @click="loadProjects">刷新</el-button>
                </div>
            </div>

            <div v-loading="loading" class="projects-container">
                <div v-for="project in filteredProjects" :key="project.id" class="project-card"
                    @click="viewProject(project)">
                    <div class="project-card-header" @click.stop>
                        <div class="project-title">
                            <span class="title-indicator"></span>
                            <span class="title-text">{{ project.name || '未命名' }}</span>
                        </div>
                        <el-dropdown trigger="click" @command="cmd => handleProjectCommand(cmd, project)"
                            @click.native.stop>
                            <span class="project-more" @click.stop>
                                <i class="el-icon-more"></i>
                            </span>
                            <el-dropdown-menu slot="dropdown">
                                <el-dropdown-item command="rename">
                                    <i class="el-icon-edit"></i> 重命名
                                </el-dropdown-item>
                                <el-dropdown-item command="delete" divided>
                                    <i class="el-icon-delete"></i> 删除
                                </el-dropdown-item>
                            </el-dropdown-menu>
                        </el-dropdown>
                    </div>

                    <div class="project-content">
                        <div v-if="project.cover" class="project-cover-wrapper">
                            <el-image :src="project.cover" fit="cover" class="project-cover-image">
                                <div slot="error" class="cover-placeholder">
                                    <i class="el-icon-picture-outline"></i>
                                </div>
                            </el-image>
                        </div>
                        <div class="project-info">
                            <div class="project-stats">
                                <span>场景:{{ project.videodir_num || 0 }}</span>
                                <span>音乐:{{ project.music_num || 0 }}</span>
                            </div>
                            <div class="project-meta">
                                <span>{{ formatTime(project.time) }}</span>
                                <span>ID:{{ project.id }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="project-actions" @click.stop>
                        <el-button size="small" @click.stop="viewProject(project)">
                            <i class="el-icon-edit"></i>
                            编辑项目
                        </el-button>
                        <el-button size="small" type="primary" @click.stop="viewProject(project)">
                            <i class="el-icon-video-camera"></i>
                            制作视频
                        </el-button>
                    </div>
                </div>
            </div>

            <div v-if="projects.length === 0 && !loading" class="empty-state">
                <i class="el-icon-document"></i>
                <p>暂无项目</p>
            </div>

            <div class="pagination-footer">
                <div class="pagination-info">共 {{ total }} 个项目</div>
                <el-pagination layout="total, prev, pager, next, jumper" :total="total" :page-size="pageSize"
                    :current-page.sync="currentPage" @current-change="handlePageChange" />
            </div>
        </el-card>

        <!-- 全屏开发中标志 -->
        <div v-if="showDevelopingOverlay" class="developing-overlay">
            <div class="developing-content">
                <div class="developing-icon">
                    <i class="el-icon-loading"></i>
                </div>
                <div class="developing-text">正在开发中...</div>
                <div class="developing-subtitle">功能即将上线，敬请期待</div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'Workflow',
    data() {
        return {
            projects: [],
            loading: false,
            currentPage: 1,
            pageSize: 6,
            total: 0,
            lastPage: 1,
            searchKeyword: '',
            showDevelopingOverlay: true // 控制是否显示全屏开发中标志
        }
    },
    computed: {
        filteredProjects() {
            if (!this.searchKeyword) {
                return this.projects
            }
            const keyword = this.searchKeyword.toLowerCase()
            return this.projects.filter(project => {
                const name = (project.name || '未命名').toLowerCase()
                return name.includes(keyword)
            })
        }
    },
    methods: {
        async loadProjects() {
            this.loading = true
            try {
                const res = await this.$ajax.get(`qhy/sck/sck_item_list?page=${this.currentPage}`)
                console.log('项目列表返回:', res.data)
                if (res.data.status === 200) {
                    const data = res.data.data
                    this.projects = data.data || []
                    this.total = data.total || 0
                    this.currentPage = data.current_page || 1
                    this.lastPage = data.last_page || 1
                    this.pageSize = data.per_page || 6
                } else {
                    this.$message.error(res.data.msg || '加载失败')
                }
            } catch (err) {
                console.error('load projects error:', err)
                this.$message.error('加载项目列表失败')
            } finally {
                this.loading = false
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.loadProjects()
        },
        formatTime(timestamp) {
            if (!timestamp) return '-'
            const date = new Date(timestamp * 1000)
            return date.toLocaleString('zh-CN', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
            })
        },
        handleProjectCommand(cmd, project) {
            if (cmd === 'rename') {
                this.renameProject(project)
            } else if (cmd === 'delete') {
                this.deleteProject(project)
            }
        },
        viewProject(project) {
            // 跳转到文件列表页面，传递项目 ID
            this.$router.push({
                path: '/user/workflow_files',
                query: { id: project.id }
            })
        },
        renameProject(project) {
            this.$prompt('请输入新的项目名称', '重命名项目', {
                inputValue: project.name || '',
                confirmButtonText: '确定',
                cancelButtonText: '取消',
            }).then(({ value }) => {
                if (!value) return
                this.$ajax.post('qhy/sck/rename_item', {
                    id: project.id,
                    name: value
                }, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).then(res => {
                    if (res.data.status === 200) {
                        project.name = value
                        this.$message.success('重命名成功')
                    } else {
                        this.$message.error(res.data.msg || '重命名失败')
                    }
                }).catch(() => {
                    this.$message.error('重命名失败')
                })
            }).catch(() => { })
        },
        deleteProject(project) {
            this.$confirm(`确定删除项目"${project.name || '未命名项目'}"吗？`, '删除项目', {
                confirmButtonText: '删除',
                cancelButtonText: '取消',
                type: 'warning'
            }).then(() => {
                this.$ajax.post('qhy/sck/delete_item', {
                    id: project.id
                }, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }).then(res => {
                    if (res.data.status === 200) {
                        this.$message.success('项目已删除')
                        this.loadProjects()
                    } else {
                        this.$message.error(res.data.msg || '删除失败')
                    }
                }).catch(() => {
                    this.$message.error('删除失败')
                })
            }).catch(() => { })
        },
    },
    created() {
        this.loadProjects()
    }
}
</script>

<style scoped>
.workflow-page {
    padding: 10px;
    min-height: calc(100vh - 120px);
    position: relative;
}

.projects-container {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    padding: 20px 0;
}

.project-card {
    background: #fff;
    border: 1px solid #e4e7ed;
    border-radius: 8px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.project-card:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
}

.project-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
}

.project-title {
    display: flex;
    align-items: center;
    flex: 1;
}

.title-indicator {
    width: 4px;
    height: 16px;
    background: #409eff;
    border-radius: 2px;
    margin-right: 8px;
}

.title-text {
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.project-more {
    cursor: pointer;
    color: #909399;
    font-size: 18px;
    padding: 4px;
    transition: color 0.3s;
}

.project-more:hover {
    color: #409eff;
}

.project-content {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
}

.project-cover-wrapper {
    flex-shrink: 0;
}

.project-cover-image {
    width: 80px;
    height: 60px;
    border-radius: 6px;
    overflow: hidden;
}

.cover-placeholder {
    width: 80px;
    height: 60px;
    background: #f5f7fa;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    color: #c0c4cc;
    font-size: 24px;
}

.project-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}

.project-stats {
    font-size: 14px;
    color: #606266;
    margin-bottom: 8px;
}

.project-stats span {
    margin-right: 12px;
}

.project-meta {
    font-size: 12px;
    color: #909399;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.project-actions {
    display: flex;
    gap: 8px;
    margin-top: 12px;
}

.project-actions .el-button {
    flex: 1;
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #909399;
}

.empty-state i {
    font-size: 48px;
    margin-bottom: 16px;
    display: block;
}

.empty-state p {
    font-size: 14px;
}

.pagination-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.pagination-info {
    color: #666;
    font-size: 14px;
}

/* 响应式布局 */
/* 默认：2列（小屏幕） */
.projects-container {
    grid-template-columns: repeat(2, 1fr);
}

/* 中等屏幕：3列 */
@media (min-width: 992px) {
    .projects-container {
        grid-template-columns: repeat(3, 1fr);
    }
}

/* 大屏幕：4列 */
@media (min-width: 1400px) {
    .projects-container {
        grid-template-columns: repeat(4, 1fr);
    }
}

/* 超小屏幕：1列 */
@media (max-width: 640px) {
    .projects-container {
        grid-template-columns: 1fr;
    }

    .project-content {
        flex-direction: column;
    }

    .project-cover-wrapper {
        align-self: center;
    }
}

/* 全屏开发中标志 */
.developing-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    backdrop-filter: blur(4px);
    border-radius: 4px;
}

.developing-content {
    text-align: center;
    color: #fff;
    padding: 40px;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.developing-icon {
    font-size: 64px;
    margin-bottom: 20px;
    animation: rotate 2s linear infinite;
}

.developing-icon i {
    color: #409eff;
}

.developing-text {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #fff;
}

.developing-subtitle {
    font-size: 16px;
    color: rgba(255, 255, 255, 0.8);
}

@keyframes rotate {
    from {
        transform: rotate(0deg);
    }

    to {
        transform: rotate(360deg);
    }
}
</style>



// WEBPACK FOOTER //
// src/components/User/Workflow.vue


// WEBPACK FOOTER //
// src/components/User/Workflow.vue